import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { keys } from "@/api/hooks";
import { notificationKeys } from "@/api/notifications";
import { SseHttpError, streamSse } from "@/lib/sse";
import {
  clearSessionProgress,
  recordSessionProgress,
  resetSessionProgress,
} from "@/features/sessions/sessionProgress";
import { recordTyping } from "@/features/sessions/typingPresence";
import { draftKeys } from "@/api/documentDrafts";
import { recordDraftEdited } from "@/features/drafts/draftEdits";

/** Subscribe to the server's session-event SSE stream and react to
 *  every event by invalidating the relevant React Query caches.
 *
 *  Mount once, globally (AppShell). Without this, the sidebar's
 *  session list, the currently-viewed session card, and the
 *  session-level fields (title, collaborators, linked projects)
 *  stay stale until the user's next focus/reload. With it, any
 *  mutation anywhere in the backend — the user's own actions OR
 *  a collaborator's edit on a shared session — shows up live.
 *
 *  Simple by design: the SSE carries only a ``session_id`` per
 *  event, the handler invalidates, and the next render refetches
 *  via the existing API clients. Browser's EventSource
 *  automatically reconnects on drop; the handler is idempotent so
 *  a replayed event after reconnect is safe.
 */
export function useSessionEventStream(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;

    // The bus has no replay buffer: events published while the stream
    // was down (server restart, proxy idle close, the 1–3 s backoff) are
    // simply gone, and window-focus refetch is off globally. So every
    // RE-connect refetches the session list + every open session detail
    // — that is what makes a pending confirm card that arrived during
    // the gap show up without a manual reload.
    let connections = 0;
    const catchUp = () => {
      qc.invalidateQueries({ queryKey: keys.sessions });
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      // A phase's clear may have been lost with the gap: better no status
      // line than a stale "waiting for capacity". The next phase comes back.
      resetSessionProgress();
    };
    const loop = async () => {
      while (!stopped) {
        try {
          connections += 1;
          if (connections > 1) catchUp();
          await streamSse(
            "/api/sessions/events",
            (raw) => {
              const kind = raw.event ?? "";
              let sessionId: string | null = null;
              if (raw.data) {
                try {
                  sessionId = (JSON.parse(raw.data) as { session_id?: string })
                    .session_id ?? null;
                } catch {
                  sessionId = null;
                }
              }
              if (kind === "session.deleted" || kind === "session.updated" || kind === "session.created") {
                // Drop the sidebar list; the session-page view
                // gets the detail refetch if it's the one that
                // changed.
                qc.invalidateQueries({ queryKey: keys.sessions });
                qc.invalidateQueries({ queryKey: ["sessions", "archived"] });
                if (sessionId) {
                  // The turn's end publishes one: a turn phase whose clear
                  // was lost must not stand after it. (A send's stays: a
                  // brand notice can update the row mid-send.)
                  if (kind === "session.updated") clearSessionProgress(sessionId, "turn");
                  qc.invalidateQueries({ queryKey: keys.session(sessionId) });
                  // The row changed because something happened in the
                  // session (a decision answered, the intent switched);
                  // the transcript usually gained a row with it.
                  qc.invalidateQueries({ queryKey: keys.messages(sessionId) });
                  // A brand-profile switch by another participant, or an
                  // admin deleting the session's profile, changes the
                  // theme variants on offer (per-profile tokens, cached
                  // for 5 minutes). Only the switcher's own PATCH
                  // refreshes them otherwise.
                  qc.invalidateQueries({
                    queryKey: ["docgen", "theme-variants", sessionId],
                  });
                }
              } else if (kind === "notification.created" || kind === "notification.changed") {
                // The bell's count and list refetch; the bell itself marks
                // a row read at once when its session is on screen.
                qc.invalidateQueries({ queryKey: notificationKeys.all });
              } else if (kind === "session.progress" && sessionId && raw.data) {
                // What a send or a turn start is doing (./sessionProgress).
                // A turn this view hasn't seen yet: refetch the session so
                // it finds the active turn and joins its stream.
                try {
                  const { newTurn } = recordSessionProgress(sessionId, JSON.parse(raw.data));
                  if (newTurn) qc.invalidateQueries({ queryKey: keys.session(sessionId) });
                } catch {
                  /* malformed payload — ignore */
                }
              } else if (kind === "session.draft_edited" && sessionId && raw.data) {
                // A participant saved a markdown draft in the app. An open
                // editor of it refetches; who saved it words the offer to
                // reload when the reader has unsaved text of their own.
                try {
                  const p = JSON.parse(raw.data) as {
                    name?: string;
                    sha256?: string;
                    user_id?: string;
                  };
                  if (p.name) {
                    if (p.sha256) {
                      recordDraftEdited(sessionId, p.name, {
                        sha256: p.sha256,
                        userId: p.user_id ?? null,
                      });
                    }
                    qc.invalidateQueries({ queryKey: draftKeys.draft(sessionId, p.name) });
                  }
                } catch {
                  /* malformed payload — ignore */
                }
              } else if (kind === "session.typing" && sessionId && raw.data) {
                // Live collaborator presence — no cache work, just feed the
                // ephemeral typing store SessionView renders from.
                try {
                  const p = JSON.parse(raw.data) as {
                    user_id?: string;
                    name?: string;
                    chars?: number;
                    draft?: string;
                  };
                  if (p.user_id) {
                    recordTyping(sessionId, {
                      userId: p.user_id,
                      name: p.name ?? "Someone",
                      chars: p.chars ?? 0,
                      draft: p.draft ?? "",
                    });
                  }
                } catch {
                  /* malformed payload — ignore */
                }
              }
              // ``hello`` and any future unknown events: ignore.
            },
            controller.signal,
          );
          // streamSse returned normally — the server closed the
          // stream. Back off briefly and reconnect.
          if (!stopped) {
            await new Promise((r) => setTimeout(r, 1000));
          }
        } catch (err) {
          if (controller.signal.aborted) return;
          // 401/403 → the session cookie is dead. Reconnecting just
          // hammers /api/sessions/events with the same dead cookie and
          // floods the logs with 401s. Stop the loop; the app's normal
          // auth handling routes to login, and a re-authenticated
          // remount restarts this effect.
          if (
            err instanceof SseHttpError &&
            (err.status === 401 || err.status === 403)
          ) {
            stopped = true;
            return;
          }
          // Transport error (server restart, network blip). Back
          // off a little longer to avoid hammering the endpoint
          // during an outage, then reconnect.
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
    };

    void loop();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, [qc]);
}
